import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { t } = useI18n();
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [totpCode, setTotpCode] = useState("");
  const [twofaRequired, setTwofaRequired] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form.email, form.password, twofaRequired ? totpCode : undefined);
      toast.success("Welcome back.");
      // If the user landed here from a protected AI route, bounce them
      // back to the page they originally tried to visit.
      const params = new URLSearchParams(location.search);
      const next = params.get("next");
      const safeNext = next && next.startsWith("/") ? next : "/portal";
      nav(safeNext, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const headers = err?.response?.headers || {};
      const twofaFlag = headers["x-auth-2fa-required"] === "1";
      const verifyFlag = headers["x-auth-verify-required"] === "1";
      if (status === 403 && twofaFlag) {
        setTwofaRequired(true);
        toast("Enter your 6-digit two-factor code");
      } else if (status === 403 && verifyFlag) {
        // Account exists but email isn't verified yet — bounce to /verify
        // so the user can complete the OTP flow. Preserve ?next= so they
        // still land back on the protected AI page after verifying.
        toast.error(formatErr(err.response?.data?.detail));
        const next = new URLSearchParams(location.search).get("next");
        const nextSuffix = next ? `&next=${encodeURIComponent(next)}` : "";
        nav(`/verify?email=${encodeURIComponent(form.email)}&pending=1${nextSuffix}`);
      } else {
        toast.error(formatErr(err.response?.data?.detail));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="login-page" className="max-w-md mx-auto px-6 py-24">
      <p className="eyebrow">{t.auth.loginTitle}</p>
      <h1 className="font-display text-4xl mt-3 tracking-tight">{t.auth.loginLead}</h1>

      <form onSubmit={onSubmit} className="mt-12 grid gap-5" data-testid="login-form">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.auth.email}</Label>
          <Input
            type="email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            dir="auto" data-testid="login-email" className="mt-2 rounded-sm h-11"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.auth.password}</Label>
          <Input
            type="password" required value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="login-password" className="mt-2 rounded-sm h-11"
          />
        </div>
        {twofaRequired && (
          <div data-testid="login-2fa-row">
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">
              Two-Factor Code
            </Label>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
              data-testid="login-totp"
              placeholder="123456"
              className="mt-2 rounded-sm h-11 tracking-[0.5em] text-center"
            />
            <p className="text-xs text-slate-500 mt-2">
              From your authenticator app (Google Authenticator, Authy, 1Password…)
            </p>
          </div>
        )}
        <Button
          type="submit" disabled={busy}
          data-testid="login-submit"
          className="rounded-sm h-12 bg-slate-900 hover:bg-slate-800 text-white"
        >
          {busy ? "…" : t.auth.submitLogin}
        </Button>
        <div className="flex items-center justify-between text-sm mt-2">
          <Link
            to="/forgot-password"
            data-testid="login-forgot"
            className="text-slate-500 hover:text-slate-900 underline underline-offset-4"
          >
            {t.auth.forgotPwd || "Forgot password?"}
          </Link>
          <Link
            to={`/register${location.search}`}
            className="text-slate-900 underline underline-offset-4"
          >
            {t.auth.register}
          </Link>
        </div>
      </form>
    </div>
  );
}
